import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  PALETTES,
  type Palette,
  type PaletteName,
  type Role,
  type SidebarMode,
  type ViewMode,
} from './tokens';

export interface AppContextValue {
  pal: Palette;
  paletteName: PaletteName;
  viewMode: ViewMode;
  sidebar: SidebarMode;
  role: Role;
}

const DEFAULT_VALUE: AppContextValue = {
  pal: PALETTES.navyCream,
  paletteName: 'navyCream',
  viewMode: 'ledger',
  sidebar: 'labels',
  role: 'viewer',
};

export const AppCtx = createContext<AppContextValue>(DEFAULT_VALUE);

export function useApp(): AppContextValue {
  return useContext(AppCtx);
}

export interface AppProviderProps {
  paletteName?: PaletteName;
  viewMode?: ViewMode;
  sidebar?: SidebarMode;
  role?: Role;
  children: ReactNode;
}

export function AppProvider({
  paletteName = 'navyCream',
  viewMode = 'ledger',
  sidebar = 'labels',
  role = 'viewer',
  children,
}: AppProviderProps) {
  const value = useMemo<AppContextValue>(
    () => ({
      pal: PALETTES[paletteName],
      paletteName,
      viewMode,
      sidebar,
      role,
    }),
    [paletteName, viewMode, sidebar, role],
  );
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
